import { cloneElement, useSyncExternalStore, type ReactNode } from "react";
import { createPortal } from "react-dom";

import { cn } from "../lib/utils";
import { usePageBarSlot, usePageBarTrail } from "./PageBar";

/**
 * The one page-top pattern: a title, what it counts, and the page's action, on one line.
 *
 * Inside the console shell it renders into the top bar, so a page spends no band of its
 * own on its name. The description is for assistive technology only: a page explains
 * itself through what it shows, not through a sentence under its title.
 */
export function PageHeader({
  eyebrow,
  title,
  description,
  meta,
  actions,
  className,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  meta?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  const slot = usePageBarSlot();
  // The way back sits before the title; a step that is this page's own name is the title.
  const trail = usePageBarTrail().filter((crumb) => crumb.label !== title);
  const phone = usePhoneWidth();
  // On a phone the top bar holds the title alone; what the page counts goes to a strip.
  const barMeta = slot && phone ? undefined : meta;
  const header = (
    <header
      data-slot="page-header"
      className={cn(
        "flex min-w-0 flex-1 items-center gap-x-3 gap-y-1",
        slot ? "h-full" : "flex-wrap border-b border-border pb-2",
        className,
      )}
    >
      <div
        data-slot="page-header-copy"
        className="flex min-w-0 shrink items-baseline gap-2 max-sm:min-w-20"
      >
        {eyebrow ? <span className="sr-only">{eyebrow}</span> : null}
        {slot && trail.length > 0 ? (
          <nav
            aria-label="Breadcrumb"
            data-slot="page-header-trail"
            className="flex min-w-0 shrink-[2] items-baseline gap-1.5 text-body text-muted-foreground max-sm:hidden"
          >
            {trail.map((crumb) => (
              <span key={crumb.label} className="flex min-w-0 items-baseline gap-1.5">
                {crumb.link
                  ? cloneElement(crumb.link, {
                      className: "truncate rounded-sm outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring",
                      children: crumb.label,
                    })
                  : <span className="truncate">{crumb.label}</span>}
                <span aria-hidden="true">/</span>
              </span>
            ))}
          </nav>
        ) : null}
        <h1 className="min-w-0 truncate font-sans text-section-title font-semibold">
          {title}
        </h1>
        {description ? <p className="sr-only">{description}</p> : null}
      </div>
      {/* The title comes first: what it counts takes the width left beside it, and a fact
          that does not fit there steps out whole rather than being cut (it wraps onto a
          second line the bar does not show), so a phone keeps the page's name. The empty
          first item holds the shown line, so even the first fact can step out. */}
      {barMeta ? (
        <div
          data-slot="page-header-meta"
          className={cn(
            "flex h-6 min-w-0 shrink grow basis-0 flex-wrap items-center gap-x-1.5 overflow-hidden whitespace-nowrap before:-mr-1.5 before:h-6 before:w-0 before:content-[''] [&>*]:max-w-none [&>*]:shrink-0 font-mono text-instrument font-normal text-muted-foreground",
          )}
        >
          {barMeta}
        </div>
      ) : null}
      {actions ? (
        <div
          data-slot="page-header-actions"
          className="ml-auto flex min-w-0 shrink-0 items-center gap-1.5"
        >
          {actions}
        </div>
      ) : null}
    </header>
  );
  if (!slot) return header;
  return (
    <>
      {createPortal(header, slot)}
      {/* A phone's top bar holds the title alone, so what the page counts moves to a strip
          at the head of the page, every fact whole and the row swiped sideways. */}
      {meta && phone ? (
        <div
          data-slot="page-header-meta-strip"
          className="-mt-1 mb-2 flex min-w-0 items-center gap-x-4 overflow-x-auto whitespace-nowrap border-b border-border pb-2 [scrollbar-width:none] [&>*]:shrink-0 font-mono text-instrument font-normal text-muted-foreground"
        >
          {meta}
        </div>
      ) : null}
    </>
  );
}

/* The width below which the console is laid out for a phone (Tailwind's `sm`). */
const PHONE = "(max-width: 639px)";

function subscribePhone(onChange: () => void): () => void {
  if (typeof window === "undefined" || !window.matchMedia) return () => {};
  const query = window.matchMedia(PHONE);
  query.addEventListener?.("change", onChange);
  return () => query.removeEventListener?.("change", onChange);
}

const phoneNow = (): boolean =>
  typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(PHONE).matches;

function usePhoneWidth(): boolean {
  return useSyncExternalStore(subscribePhone, phoneNow, () => false);
}
